import { useAuthStore } from "@/store/AuthStore";
import NProgress from "nprogress";
import router from "@/router";

const API_URL = import.meta.env.VITE_API_URL || '/api';

/**
 * Parse standardized API response format.
 * Expected format: { success: boolean, data: any, error: { code, message }|null, meta: object }
 */
const parseApiResponse = (json) => {
  // If response follows the standard format
  if (typeof json === 'object' && json !== null && 'success' in json) {
    if (!json.success && json.error) {
      // Return error info for handling
      const error = new Error(json.error.message || 'Request failed');
      error.code = json.error.code;
      error.apiError = json.error;
      error.meta = json.meta;
      throw error;
    }
    // Return data with meta attached
    const result = json.data;
    if (result && typeof result === 'object' && json.meta) {
      result._meta = json.meta;
    }
    return result;
  }
  // Legacy format - return as-is
  return json;
};

// Track if we're currently refreshing the token
let isRefreshing = false;
// Store pending requests
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

const sanitizeRedirectUrl = (urlStr) => {
    try {
        const url = new URL(urlStr);
        // If the redirect points to the backend (port 8000), force it to the frontend (current origin)
        // You can adjust the port check if your backend port changes
        if (url.port === '8000') {
            const currentOrigin = new URL(window.location.href);
            url.protocol = currentOrigin.protocol;
            url.host = currentOrigin.host;
            url.port = currentOrigin.port;
            return url.toString();
        }
        return urlStr;
    } catch (e) {
        // If it's a relative path, just return it
        return urlStr;
    }
};

// Don't normalize endpoint - let backend handle it, but handle redirects manually
async function httpClient(endpoint, { body, ...config } = {}) {
    const authStore = useAuthStore();
    const isRetry = config._isRetry || false;
    
    // Remove internal flags before passing to fetch
    delete config._isRetry;
    
    // Build headers
    const headers = { 
        'Content-Type': 'application/json',
        ...config.headers 
    };
    
    // Add auth token if available
    if (authStore.user_token) {
        headers.Authorization = `Bearer ${authStore.user_token}`;
    }

    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
    const url = `${API_URL}/v1/${cleanEndpoint}`;
    
    NProgress.start();
    
    try {
        let response = await window.fetch(url, {
            method: config.method || 'GET',
            body: body ? JSON.stringify(body) : null,
            headers,
            redirect: 'manual', // Handle redirects manually to preserve auth headers
        });
        
        // Handle redirects manually - follow them with auth headers
        if (response.type === 'opaqueredirect' || (response.status >= 300 && response.status < 400)) {
            const redirectUrl = response.headers.get('Location');
            if (redirectUrl) {
                // Follow the redirect but preserve auth headers
                const safeUrl = sanitizeRedirectUrl(redirectUrl);
                console.log(`[Fetch] Following redirect from ${redirectUrl} to ${safeUrl}`);
                response = await window.fetch(safeUrl, {
                    method: config.method || 'GET',
                    body: body ? JSON.stringify(body) : null,
                    headers,
                    redirect: 'manual',
                });
            }
        }
        
        if (response.ok) {
            NProgress.done();
            
            // Handle empty response (204 No Content)
            if (response.status === 204) {
                return null;
            }
            
            // Check if response has a JSON content type
            const contentType = response.headers.get("Content-Type");
            if (contentType && contentType.includes("application/json")) {
                const json = await response.json();
                return parseApiResponse(json);
            }

            // If not JSON, return raw text or null
            const text = await response.text();
            return text || null;
        }
        
        // Handle error responses
        let errorMessage = '';
        let errorCode = null;
        try {
            const errorText = await response.text();
            const errorJson = JSON.parse(errorText);
            // Handle standardized error format
            if (errorJson.error) {
                errorCode = errorJson.error.code;
                errorMessage = errorJson.error.message;
            } else {
                errorMessage = errorJson.message || errorJson.detail || errorText;
            }
        } catch {
            errorMessage = `Request failed with status ${response.status}`;
        }

        // Create rich error object
        const apiError = new Error(errorMessage);
        apiError.code = errorCode;
        apiError.status = response.status;
        
        // If not logged in, don't try to refresh
        if (!authStore.user_loggedIn) {
            NProgress.done();
            return Promise.reject(apiError);
        }
        
        // Handle 401 Unauthorized - but only retry once
        if (response.status === 401 && !isRetry) {
            // If already refreshing, queue this request
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                }).then(() => {
                    return httpClient(endpoint, { body, ...config, _isRetry: true });
                }).catch(err => {
                    return Promise.reject(err);
                });
            }
            
            // Check if we have a refresh token
            if (!authStore.refreshToken) {
                console.warn('[Fetch] No refresh token available');
                NProgress.done();
                await authStore.logout();
                router.push('/logout?reason=token_revoked');
                return Promise.reject(new Error('Authentication required'));
            }
            
            // Start refresh
            isRefreshing = true;
            console.log('[Fetch] Refreshing token...');
            
            try {
                await authStore.refreshAccessToken();
                isRefreshing = false;
                processQueue(null, authStore.user_token);
                // Retry with new token
                return httpClient(endpoint, { body, ...config, _isRetry: true });
            } catch (refreshError) {
                console.error('[Fetch] Token refresh failed:', refreshError);
                isRefreshing = false;
                processQueue(refreshError, null);
                NProgress.done();
                await authStore.logout();
                router.push('/logout?reason=session_expired');
                return Promise.reject(new Error('Session expired'));
            }
        }
        
        // If retry still got 401, logout
        if (response.status === 401 && isRetry) {
            console.warn('[Fetch] Retry failed with 401');
            NProgress.done();
            await authStore.logout();
            router.push('/logout?reason=invalid_token');
            return Promise.reject(new Error('Authentication failed'));
        }
        
        NProgress.done();
        return Promise.reject(apiError);

    } catch (networkError) {
        NProgress.done();
        // Handle redirect errors specifically
        if (networkError.message?.includes('redirect')) {
            console.warn('[Fetch] Redirect blocked for:', endpoint);
            return Promise.reject(new Error('Request was redirected - check API endpoint'));
        }
        console.error('[Fetch] Network error:', networkError);
        throw new Error(networkError.message || 'Network request failed');
    }
}

export const useFetch = () => {
    return httpClient;
};